import { useEffect, useState } from "react";
import axios from "axios";
import { Download, Sparkles, AlertTriangle, X, Camera, Clock } from "lucide-react";

// Rendered inside the existing Admin dashboard as one tab.
// Uses the same axios instance / auth cookie the admin dashboard uses.
const API = `${process.env.REACT_APP_BACKEND_URL}/api/symptom-checker`;

const URG_STYLES = {
  emergency: "text-red-500 border-red-500/40 bg-red-500/10",
  urgent:    "text-orange-500 border-orange-500/40 bg-orange-500/10",
  soon:      "text-amber-500 border-amber-500/40 bg-amber-500/10",
  routine:   "text-emerald-500 border-emerald-500/40 bg-emerald-500/10",
};

const StatCard = ({ label, value, tint = "accent" }) => (
  <div className="rounded-2xl border border-border p-5">
    <div className="text-[10.5px] uppercase tracking-[0.22em] text-[hsl(var(--foreground))]/60">{label}</div>
    <div className={`mt-2 font-serif text-3xl text-[hsl(var(--${tint}))]`}>{value}</div>
  </div>
);

const Bar = ({ label, count, max }) => {
  const pct = max ? Math.round((count / max) * 100) : 0;
  return (
    <div className="flex items-center gap-4 text-[13px]">
      <div className="w-40 shrink-0 text-[hsl(var(--foreground))]/80 truncate">{label}</div>
      <div className="flex-1 h-2 rounded-full bg-[hsl(var(--foreground))]/8 overflow-hidden">
        <div className="h-full bg-[hsl(var(--accent))]" style={{ width: `${pct}%` }} />
      </div>
      <div className="w-12 text-right font-medium">{count}</div>
    </div>
  );
};

const SymptomAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await axios.get(`${API}/analytics`, { withCredentials: true });
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const downloadCsv = async () => {
    const res = await axios.get(`${API}/analytics/export.csv`, { withCredentials: true, responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = `symptom-assessments-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading && !data) return <div className="p-6 text-sm text-[hsl(var(--foreground))]/60">Loading analytics…</div>;
  if (err) return <div className="p-6 text-sm text-red-500">Error: {err}</div>;
  if (!data) return null;

  const maxSymptom = Math.max(1, ...data.symptoms.map((s) => s.count));
  const maxAge = Math.max(1, ...data.age_distribution.map((s) => s.count));
  const maxUrg = Math.max(1, ...data.urgency_distribution.map((s) => s.count));

  return (
    <div className="space-y-8" data-testid="admin-symptom-analytics">
      <div className="flex items-center justify-between">
        <div>
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--accent))]"><Sparkles size={12} strokeWidth={1.8} /> AI Symptom Checker</div>
          <h2 className="font-serif text-2xl mt-2">Analytics</h2>
        </div>
        <button onClick={downloadCsv} className="btn-ghost" data-testid="btn-csv-export">
          <Download size={14} strokeWidth={1.7} /> Export CSV
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total assessments" value={data.total_assessments} />
        <StatCard label="Emergency triggered" value={data.urgency_distribution.find((u) => u.urgency === "emergency")?.count || 0} tint="destructive" />
        <StatCard label="CTA conversion" value={`${data.conversion_rate_pct}%`} />
        <StatCard label="Last 30 days" value={data.daily_last_30.reduce((a, d) => a + d.count, 0)} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-border p-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--foreground))]/60 mb-5">Most common symptoms</div>
          <div className="space-y-3">
            {data.symptoms.map((s) => <Bar key={s.symptom} label={s.symptom} count={s.count} max={maxSymptom} />)}
            {data.symptoms.length === 0 && <div className="text-sm text-[hsl(var(--foreground))]/60">No data yet.</div>}
          </div>
        </div>
        <div className="rounded-2xl border border-border p-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--foreground))]/60 mb-5">Age distribution</div>
          <div className="space-y-3">
            {data.age_distribution.map((a) => <Bar key={a.age_band} label={a.age_band} count={a.count} max={maxAge} />)}
          </div>
        </div>
        <div className="rounded-2xl border border-border p-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--foreground))]/60 mb-5">Urgency distribution</div>
          <div className="space-y-3">
            {data.urgency_distribution.map((u) => <Bar key={u.urgency} label={u.urgency} count={u.count} max={maxUrg} />)}
          </div>
        </div>
        <div className="rounded-2xl border border-border p-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--foreground))]/60 mb-5">CTA clicks</div>
          <div className="space-y-3">
            {data.cta_clicks.map((c) => <Bar key={c.cta} label={c.cta} count={c.count} max={Math.max(1, ...data.cta_clicks.map((x) => x.count))} />)}
            {data.cta_clicks.length === 0 && <div className="text-sm text-[hsl(var(--foreground))]/60">No CTA clicks yet.</div>}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border p-6">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--foreground))]/60 mb-5">Daily volume — last 30 days</div>
        <div className="flex items-end gap-1 h-32">
          {data.daily_last_30.map((d) => {
            const max = Math.max(1, ...data.daily_last_30.map((x) => x.count));
            const h = Math.max(2, Math.round((d.count / max) * 100));
            return (
              <div key={d.day} className="flex-1 group relative">
                <div className="w-full bg-[hsl(var(--accent))]/70 rounded-t" style={{ height: `${h}%` }} title={`${d.day}: ${d.count}`} />
              </div>
            );
          })}
        </div>
      </div>

      <AssessmentTimeline />
    </div>
  );
};

/* ─────────────────────────── Assessment timeline + detail drawer ─────────────────────────── */

const AssessmentTimeline = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/assessments`, {
        params: filter ? { urgency: filter, limit: 100 } : { limit: 100 },
        withCredentials: true,
      });
      setItems(data.items || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const openDrawer = async (id) => {
    setSelectedId(id);
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/assessments/${id}`, { withCredentials: true });
      setDetail(data);
    } catch { setDetail(null); }
    finally { setDetailLoading(false); }
  };

  const closeDrawer = () => { setSelectedId(null); setDetail(null); };

  return (
    <div className="rounded-2xl border border-border p-6" data-testid="assessment-timeline">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--foreground))]/60">Assessment timeline</div>
          <div className="mt-1 text-[13px] text-[hsl(var(--foreground))]/60">Recent triage submissions — click any row for the full case file.</div>
        </div>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em]">
          {["", "emergency", "urgent", "soon", "routine"].map((k) => (
            <button
              key={k || "all"}
              onClick={() => setFilter(k)}
              className={`px-3 py-1.5 rounded-full border transition-colors ${filter === k ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "border-border text-[hsl(var(--foreground))]/70 hover:border-[hsl(var(--foreground))]/40"}`}
              data-testid={`filter-${k || "all"}`}
            >{k || "All"}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[hsl(var(--foreground))]/60">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-[hsl(var(--foreground))]/60">No assessments {filter && `with urgency "${filter}"`} yet.</div>
      ) : (
        <ul className="divide-y divide-border">
          {items.map((it) => (
            <li key={it.assessment_id}>
              <button
                onClick={() => openDrawer(it.assessment_id)}
                className="w-full text-left py-3 px-2 hover:bg-[hsl(var(--foreground))]/[0.03] rounded-lg flex items-center gap-4"
                data-testid={`assessment-row-${it.assessment_id}`}
              >
                <span className={`inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.16em] px-2 py-1 rounded-full border ${URG_STYLES[it.urgency] || URG_STYLES.routine}`}>
                  {it.red_flag_triggered && <AlertTriangle size={11} strokeWidth={2} />}
                  {it.urgency}
                </span>
                <span className="text-[13px] text-[hsl(var(--foreground))]/90 truncate flex-1">{it.summary || it.main_symptom}</span>
                <span className="hidden sm:inline-flex items-center gap-1 text-[11px] text-[hsl(var(--foreground))]/55">
                  {it.has_photo && <Camera size={11} strokeWidth={1.7} />}
                  {it.lang === "hi" && <span className="text-[hsl(var(--accent))]">HI</span>}
                  <Clock size={11} strokeWidth={1.7} /> {new Date(it.created_at).toLocaleString()}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {selectedId && (
        <AssessmentDrawer
          detail={detail}
          loading={detailLoading}
          onClose={closeDrawer}
        />
      )}
    </div>
  );
};

const KV = ({ label, value }) => (
  <div>
    <div className="text-[10.5px] uppercase tracking-[0.2em] text-[hsl(var(--foreground))]/50">{label}</div>
    <div className="mt-1 text-[13.5px] text-[hsl(var(--foreground))]/90 break-words">{value || "—"}</div>
  </div>
);

const AssessmentDrawer = ({ detail, loading, onClose }) => (
  <div
    className="fixed inset-0 z-[100] flex justify-end"
    role="dialog"
    aria-modal="true"
    data-testid="assessment-drawer"
  >
    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
    <aside className="relative w-full max-w-xl h-full bg-background border-l border-border overflow-y-auto shadow-[-30px_0_80px_-40px_rgba(0,0,0,0.4)]">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-md border-b border-border px-6 py-4 flex items-center justify-between">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-[hsl(var(--accent))]">Case file</div>
          <div className="mt-0.5 text-[13px] text-[hsl(var(--foreground))]/60">{detail?.assessment_id?.slice(0, 12) || "…"}</div>
        </div>
        <button onClick={onClose} className="w-9 h-9 rounded-full border border-border flex items-center justify-center hover:border-[hsl(var(--foreground))]/40" data-testid="drawer-close">
          <X size={15} strokeWidth={1.8} />
        </button>
      </div>

      {loading || !detail ? (
        <div className="p-6 text-sm text-[hsl(var(--foreground))]/60">Loading assessment…</div>
      ) : (
        <div className="p-6 space-y-6">
          <div className={`rounded-2xl border p-5 ${URG_STYLES[detail.urgency] || URG_STYLES.routine}`}>
            <div className="text-[10.5px] uppercase tracking-[0.2em] font-medium flex items-center gap-2">
              {detail.red_flag_triggered && <AlertTriangle size={12} strokeWidth={2} />}
              {detail.urgency}
              {detail.red_flag_triggered && <span className="text-[10px] normal-case tracking-normal opacity-70">· red-flag rule</span>}
            </div>
            <div className="mt-2 font-serif text-lg text-[hsl(var(--foreground))]">{detail.summary}</div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <KV label="Age" value={detail.age_band} />
            <KV label="Gender" value={detail.gender} />
            <KV label="Eye" value={detail.eye_side} />
            <KV label="Main symptom" value={detail.main_symptom} />
            <KV label="Language" value={detail.lang === "hi" ? "Hindi" : "English"} />
            <KV label="Submitted" value={new Date(detail.created_at).toLocaleString()} />
          </div>

          {detail.photo_url && (
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.2em] text-[hsl(var(--foreground))]/50 mb-2">Patient-attached photo</div>
              <a href={detail.photo_url} target="_blank" rel="noopener noreferrer">
                <img src={detail.photo_url} alt="Patient uploaded eye photo" className="w-full rounded-xl border border-border object-cover" />
              </a>
            </div>
          )}

          {detail.followups && Object.keys(detail.followups).length > 0 && (
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.2em] text-[hsl(var(--foreground))]/50 mb-2">Follow-up answers</div>
              <ul className="text-[13px] text-[hsl(var(--foreground))]/85 space-y-1.5 border border-border rounded-xl p-4">
                {Object.entries(detail.followups).map(([k, v]) => (
                  <li key={k} className="flex justify-between gap-4">
                    <span className="text-[hsl(var(--foreground))]/60">{k.replace(/-/g, " ")}</span>
                    <span className="font-medium text-right">{typeof v === "string" ? v : JSON.stringify(v)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {detail.medical_history?.length > 0 && (
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.2em] text-[hsl(var(--foreground))]/50 mb-2">Medical history</div>
              <div className="flex flex-wrap gap-2">
                {detail.medical_history.map((h) => (
                  <span key={h} className="text-[12px] px-3 py-1 rounded-full border border-border">{h}</span>
                ))}
              </div>
            </div>
          )}

          {detail.possible_conditions?.length > 0 && (
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.2em] text-[hsl(var(--foreground))]/50 mb-2">AI reasoning — possible conditions</div>
              <ul className="text-[13px] text-[hsl(var(--foreground))]/85 space-y-1.5">
                {detail.possible_conditions.map((c) => (
                  <li key={c} className="flex items-start gap-2"><span className="mt-2 w-1 h-1 rounded-full bg-[hsl(var(--accent))]" /> {c}</li>
                ))}
              </ul>
            </div>
          )}

          {detail.why_it_matters && (
            <KV label="Why this matters" value={detail.why_it_matters} />
          )}
          {detail.recommended_action && (
            <KV label="Recommended action" value={detail.recommended_action} />
          )}
          {detail.warning_signs?.length > 0 && (
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.2em] text-[hsl(var(--foreground))]/50 mb-2">Warning signs the patient was told</div>
              <ul className="text-[13px] text-[hsl(var(--foreground))]/85 space-y-1.5">
                {detail.warning_signs.map((w) => (
                  <li key={w} className="flex items-start gap-2"><AlertTriangle size={12} className="mt-1 text-red-500 shrink-0" strokeWidth={1.8} /> {w}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </aside>
  </div>
);

export default SymptomAnalytics;
